import cx from 'classnames';
import * as React from 'react';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Image from 'components/Image';
import Row from 'components/Row';
import Text from 'components/Text';
import LinkConstants from 'constants/LinkConstants';
import EngagementRatings, { EngagementRating } from 'organizations/constants/EngagementRatings';

import OrganizationEngagementViewHeading from './OrganizationEngagementViewHeading';

import './OrganizationEngagementViewEngagementBox.scss';

type Props = {
  eventKey: string;
  name?: string;
  schoolRating?: EngagementRating | undefined;
  schoolsActivePercent: number;
  schoolsEmergingPercent: number;
  schoolsEngagedPercent: number;
  schoolsGrowingPercent: number;
  utmParams: string;
}

export default function OrganizationEngagementViewEngagementBox({
  eventKey,
  name,
  schoolRating,
  schoolsActivePercent,
  schoolsEmergingPercent,
  schoolsEngagedPercent,
  schoolsGrowingPercent,
  utmParams,
}: Props) {
  return (
    <Box className="OrganizationEngagementViewEngagementBox">
      <BoxBody>
        <div className="OrganizationEngagementViewEngagementBox-inner">
          <Text
            align="left"
            element="section"
            className="OrganizationEngagementViewEngagementBox-inner-body">
            <OrganizationEngagementViewHeading>
              School engagement across {name}
            </OrganizationEngagementViewHeading>
            <Text element="p" size="medium">
              {schoolRating === 'emerging' && (
                <React.Fragment>
                  <b>{schoolsEmergingPercent}%</b> of schools in the district have the
                  engagement rating <b>Emerging</b>, compared to <b>57%</b> of schools
                  nationwide.
                </React.Fragment>
              )}
              {schoolRating === 'growing' && (
                <React.Fragment>
                  <b>{schoolsGrowingPercent}%</b> of schools in the district have the
                  engagement rating <b>Growing</b>, compared to <b>20%</b> of schools
                  nationwide.
                </React.Fragment>
              )}
              {schoolRating === 'active' && (
                <React.Fragment>
                  <b>{schoolsActivePercent}%</b> of schools in the district have the
                  engagement rating <b>Active</b>, compared to <b>14%</b> of schools
                  nationwide.
                </React.Fragment>
              )}
              {schoolRating === 'engaged' && (
                <React.Fragment>
                  <b>{schoolsEngagedPercent}%</b> of schools in the district have the
                  engagement rating <b>Engaged</b>, compared to <b>9%</b> of schools
                  nationwide.
                </React.Fragment>
              )}
            </Text>
            <Button
              href={`${LinkConstants.ENGAGEMENT_RATINGS}?${utmParams}&utm_content=footer`}
              size="medium"
              eventKey={`${eventKey}.content_footer.cta.click`}>
              Find a school
            </Button>
          </Text>
          <Text
            align="center"
            element="div"
            size="small"
            className="OrganizationEngagementViewEngagementBox-inner-figure">
            <Row size="small">
              <div className="OrganizationEngagementViewEngagementBox-chart">
                {EngagementRatings.RATINGS.map(item => {
                  let percentage = schoolsEmergingPercent;

                  if (item === 'growing') {
                    percentage = schoolsGrowingPercent;
                  } else if (item === 'active') {
                    percentage = schoolsActivePercent;
                  } else if (item === 'engaged') {
                    percentage = schoolsEngagedPercent;
                  }

                  return (
                    <div key={item} className="OrganizationEngagementViewEngagementBox-barWrap">
                      <div className="OrganizationEngagementViewEngagementBox-bar">
                        <div className="OrganizationEngagementViewEngagementBox-bar-spacer" />
                        <div
                          style={{ height: `${percentage}%` }}
                          className={cx(
                            'OrganizationEngagementViewEngagementBox-bar-amount',
                            `OrganizationEngagementViewEngagementBox-bar-amount--${item}`
                          )}>
                          <Text
                            weight="semibold"
                            className={cx(
                              'OrganizationEngagementViewEngagementBox-bar-label',
                              `OrganizationEngagementViewEngagementBox-bar-label--${item}`
                            )}>
                            {percentage}%
                          </Text>
                        </div>
                      </div>
                      <Image src={EngagementRatings.BADGES[item].svg} width={42} />
                    </div>
                  );
                })}
              </div>
            </Row>
            School engagement ratings
          </Text>
        </div>
      </BoxBody>
    </Box>
  );
}
